import { createElement, useState } from 'react';

import { VBaseInput } from '../VBaseInput';
import { VBasePopover } from '../VBasePopover';
import { POPOVER_PLACEMENT } from '../VBasePopover/constant';
import { VFlex } from '../VFlex';
import { DateRange } from '../asset';
import { joinCn } from '../util';
import VDatePickerContent from './VDatePickerContent';
import { VDatePickerContextProvider } from './VDatePickerContextProvider';
import VDatePickerInput from './VDatePickerInput';
import VDateRangePickerContent from './VDateRangePickerContent';
import VDateRangePickerInput from './VDateRangePickerInput';
import { DATEPICKER_MODE } from './constant';
import style from './style';
import type { VDatePickerContextValue, VDatePickerProps } from './type';

const VDatePicker = <M extends DATEPICKER_MODE | Lowercase<DATEPICKER_MODE>>({
  className,
  mode,
  placement = POPOVER_PLACEMENT.BOTTOM_LEFT,
  value,
  onChange,
  ref,
  ...rest
}: VDatePickerProps<M> &
  Omit<React.ComponentPropsWithRef<'div'>, 'onChange'>) => {
  const [visible, setVisible] = useState(false);
  const onClose = () => setVisible(false);

  return (
    <VDatePickerContextProvider
      mode={mode}
      value={value as VDatePickerContextValue<M>['value']}
      onChange={onChange}
    >
      <VBasePopover
        className={joinCn('rounded-md')}
        content={() =>
          createElement(
            mode === DATEPICKER_MODE.RANGE
              ? (VDateRangePickerContent as any)
              : VDatePickerContent,
            {
              value,
              onClose,
              onSelect: (newValue: Date | [Date, Date]) => {
                onClose();
                onChange?.(newValue as any);
              },
            },
          )
        }
        disabled={rest.disabled || rest.readOnly}
        placement={placement}
        trigger="click"
        visible={visible}
        onUpdateVisible={setVisible}
      >
        <div>
          <VBaseInput className={joinCn(className)} {...rest}>
            <VFlex
              ref={ref}
              className="w-fit"
              items="center"
              justify="start"
              gap={0.25}
            >
              {mode === DATEPICKER_MODE.RANGE ? (
                <VDateRangePickerInput
                  disabled={rest.disabled}
                  fromDatePlaceholder={rest.fromDatePlaceholder}
                  toDatePlaceholder={rest.toDatePlaceholder}
                  valid={rest.valid}
                />
              ) : (
                <VDatePickerInput
                  disabled={rest.disabled}
                  placeholder={rest.placeholder}
                  valid={rest.valid}
                />
              )}
              <DateRange
                className={style.vDatePickerIcon({ size: rest.size })}
              />
            </VFlex>
          </VBaseInput>
        </div>
      </VBasePopover>
    </VDatePickerContextProvider>
  );
};

export default VDatePicker;
